import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'framer-motion';
import { Slice } from 'gatsby';
import { IS_PRO } from 'gatsby-theme-hem-store/src/constants';
import { Text, TextColor, TextVariant, TextWeight, transition } from 'hem-ui';
import { X } from 'lucide-react';
import React, { Suspense, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { Touchable } from '../Touchable';
import {
  container,
  header,
  headerLeft,
  headerRight,
  left,
  overlay,
  right,
} from './Panels.module.scss';

const Cart = React.lazy(() => import('./Cart'));
const AlphabetaBaseSelectionPanel = React.lazy(
  () => import('./Alphabeta/AlphabetaBaseSelectionPanel'),
);
const AlphabetaBottomSelectionPanel = React.lazy(
  () => import('./Alphabeta/AlphabetaBottomSelectionPanel'),
);
const AlphabetaTopSelectionPanel = React.lazy(
  () => import('./Alphabeta/AlphabetaTopSelectionPanel'),
);

type PanelStore = {
  currentlyOpenPanel: PanelEnum;
  setCurrentlyOpenPanel: (panel: PanelEnum) => void;
};

export enum PanelEnum {
  None,
  Menu,
  Cart,
  AlphabetaTopSelection,
  AlphabetaBottomSelection,
  AlphabetaBaseSelection,
}

export const usePanelStore = createWithEqualityFn<PanelStore>(
  (set) => ({
    currentlyOpenPanel: PanelEnum.None,
    setCurrentlyOpenPanel: (panel: PanelEnum) =>
      set({ currentlyOpenPanel: panel }),
  }),
  shallow,
);

enum DirectionEnum {
  Left,
  Right,
}

const PanelContainer: React.FC<{
  children: JSX.Element;
  direction: DirectionEnum;
}> = ({ children, direction }): JSX.Element => {
  const { currentlyOpenPanel, setCurrentlyOpenPanel } = usePanelStore();
  const [panelHeaderTitle, setPanelHeaderTitle] = useState('');

  useEffect(() => {
    switch (currentlyOpenPanel) {
      case PanelEnum.Menu:
        setPanelHeaderTitle('Imaginative designs of obsessive quality');
        break;
      case PanelEnum.Cart:
        setPanelHeaderTitle(IS_PRO ? 'Quote' : 'Shopping bag');
        break;
      case PanelEnum.AlphabetaTopSelection:
        setPanelHeaderTitle('Alphabeta top shade and color');
        break;
      case PanelEnum.AlphabetaBottomSelection:
        setPanelHeaderTitle('Alphabeta bottom shade and color');
        break;
      case PanelEnum.AlphabetaBaseSelection:
        setPanelHeaderTitle('Alphabeta base');
        break;
    }
  }, [currentlyOpenPanel]);

  useEffect(() => {
    if (currentlyOpenPanel === PanelEnum.None) {
      window.Intercom &&
        window.Intercom('update', {
          hide_default_launcher: false,
        });
    } else {
      window.Intercom &&
        window.Intercom('update', {
          hide_default_launcher: true,
        });
    }
  }, [currentlyOpenPanel]);

  const closePanel = () => {
    setCurrentlyOpenPanel(PanelEnum.None);
  };

  return (
    <FocusTrap focusTrapOptions={{ allowOutsideClick: true }}>
      <motion.div
        className={classNames(container, {
          [left]: direction === DirectionEnum.Left,
          [right]: direction === DirectionEnum.Right,
        })}
        initial={
          direction === DirectionEnum.Left
            ? { transform: 'translate3d(-100%, 0, 0)' }
            : { transform: 'translate3d(100%, 0, 0)' }
        }
        animate={
          // prettier-ignore
          direction === DirectionEnum.Left
          ? { transform: 'translate3d(0%, 0, 0)' }
          : { transform: 'translate3d(0%, 0, 0)' }
        }
        exit={
          direction === DirectionEnum.Left
            ? { transform: 'translate3d(-100%, 0, 0)' }
            : { transform: 'translate3d(100%, 0, 0)' }
        }
        {...{ transition }}
      >
        <div
          className={classNames(header, {
            [headerLeft]: direction === DirectionEnum.Left,
            [headerRight]: direction === DirectionEnum.Right,
          })}
        >
          <Text
            variant={TextVariant.BODY}
            weight={TextWeight.BOLD}
            color={TextColor.GRAY_DARK}
            tag="div"
            className="line-clamp-1"
          >
            {panelHeaderTitle}
          </Text>
          <Touchable onClick={closePanel} aria-label="Close" className="group">
            <X
              size={20}
              className="text-black-400 group-hover:text-black-600"
            />
          </Touchable>
        </div>
        {children}
      </motion.div>
    </FocusTrap>
  );
};

export const Panels: React.FC = (): JSX.Element => {
  const { currentlyOpenPanel, setCurrentlyOpenPanel } = usePanelStore();

  const closePanel = () => {
    setCurrentlyOpenPanel(PanelEnum.None);
  };

  return (
    <>
      <AnimatePresence>
        {currentlyOpenPanel !== PanelEnum.None && (
          <motion.div
            className={overlay}
            onClick={closePanel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            {...{ transition }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {currentlyOpenPanel === PanelEnum.Menu && (
          <PanelContainer direction={DirectionEnum.Left}>
            <Slice alias="main-menu" />
          </PanelContainer>
        )}
        {currentlyOpenPanel === PanelEnum.Cart && (
          <PanelContainer direction={DirectionEnum.Right}>
            <Suspense fallback={null}>
              <Cart />
            </Suspense>
          </PanelContainer>
        )}
        {currentlyOpenPanel === PanelEnum.AlphabetaTopSelection && (
          <PanelContainer direction={DirectionEnum.Right}>
            <Suspense fallback={null}>
              <AlphabetaTopSelectionPanel />
            </Suspense>
          </PanelContainer>
        )}
        {currentlyOpenPanel === PanelEnum.AlphabetaBottomSelection && (
          <PanelContainer direction={DirectionEnum.Right}>
            <Suspense fallback={null}>
              <AlphabetaBottomSelectionPanel />
            </Suspense>
          </PanelContainer>
        )}
        {currentlyOpenPanel === PanelEnum.AlphabetaBaseSelection && (
          <PanelContainer direction={DirectionEnum.Right}>
            <Suspense fallback={null}>
              <AlphabetaBaseSelectionPanel />
            </Suspense>
          </PanelContainer>
        )}
      </AnimatePresence>
    </>
  );
};
